/**
 * ** A note about Bot Scripts **
 * Bot Scripts, simply referred to as 'script' are backend scripted routines
 * A script is a mode the bot goes into, that will then respond to input based on 
 * what is expected by the script. 
 * For example the meaning of 'yes' might be in response to a specific question in the script
 * Then there will be a follow-up response.
 * The backend decides when and whether we are in a 'script'. 
 * Menu items can have the property 'script', and this can trigger a script.
 * The scripts can be found in `deploy/application/library/bot/scripts`
 * The Bot frontend is passive to scripts but what is important is that if the API returns the property 'script'
 * then the frontend must relay this back to the API when the user responds, either in a comment or by clicking a menu button
 * If the menu button has its own script property this takes precedence. But if it is null then any script passed by the API
 * should be returned to the API.
 * This is critical to tell the backend that we are still in the script. 
 * Otherwise the response would be received out of context, and would likely result in a 'I do not understand this' response.
 */

angular.module("bot", [
    "core",
    "constants",
    "ngSanitize",
    "ngAnimate",
    "LocalStorageModule",
    "videos"
  ])

    .config(($httpProvider, $logProvider, $qProvider) => {
      "use strict";
      $httpProvider.defaults.headers.post["Content-Type"] = "application/json";
      $logProvider.debugEnabled(false);
      $qProvider.errorOnUnhandledRejections(false);
    })

    .constant("CONFIGPATH", null)

    .constant("BOT_EVENTS", {
      chatSessionReady: "chat_sess_r",
      chatSessionRefreshed: "chat_sess_u",
      doReset: "chat_sess_reset",
      menuStatusEvent: "e_menu_status",
      scrollToEnd: "e_scroll",
    })

    .directive("ngBot", ($interval, $location, $rootScope, $timeout, Bot, BOT_EVENTS, ChatSession, EVENTS, System) => {

      return {
        restrict: "A",
        templateUrl: "/angularjs/partials/bot-panel.html",
        replace: true,
        scope: {
          viewMode: "@ngBot"
        },
        link: scope => {

          let transcriptLength = null;
          let activateFromExternalEvent = null;
          let scrollTimer = null;
          let bubbleInitTimer = null;
          let isAlwaysAwakeMode = false;
          let showingTyping = false;

          // default view mode is the floating panel
          scope.floating = scope.viewMode === "floating";
          isAlwaysAwakeMode = scope.viewMode === "sidebar";
          scope.bot = Bot.getStatus();
          scope.session = ChatSession.session;
          scope.showTyping = ChatSession.showTyping;

          // check for showTyping and scroll to max
          // for some reason this won't work on a watcher, simply by binding scope.showTyping to ChatSession.showTyping
          $interval(() => {
            scope.showTyping = ChatSession.showTyping;
            if(showingTyping !== scope.showTyping){
              showingTyping = scope.showTyping;
              if(showingTyping){
                $timeout(() => {
                  scope.scrollToMax();
                }, 10);
              } else {
                $timeout(() => {
                  scope.scrollToMax();
                }, 400);
              }
            }
          }, 300);


          // this is to scroll past the end of the messages when the menu appears
          scope.$watch("session.menuOnlyMode", newMode => {
            if (!newMode) {
              $timeout(() => {
                scope.scrollToMax();
              }, 500);
            }
          });

          scope.$watch("session.isStalled", isStalled => {
            if(isStalled){
              const onStalledSuggestion = ChatSession.onStalledSuggestion;
              scope.injectSuggestion(onStalledSuggestion.content, onStalledSuggestion.key);
            }
          });

          scope.suggestions = [];
          scope.botContext = scope.botContext || {};
          scope.inputIsEmpty = true; // see directive ng-user-input, actual value of userInput is private to that directive
          scope.disableMenu = false;
          scope.ready = false;

          // - - scope methods - - //

          scope.togglePanel = flag => {
            ChatSession.togglePanel(flag);
            $rootScope.$broadcast(EVENTS.botPanelState, { panelOpen: flag });
            scope.scrollToMax();
            scope.dismissBubble();
          }

          scope.dismissBubble = () => {
            ChatSession.dismissBubble();
            if(bubbleInitTimer){
              $timeout.cancel(bubbleInitTimer);
            }
            if(scope.bubbleClasses){
              scope.bubbleClasses = "show animated bounceOutDown";  
              $timeout(() => scope.bubbleClasses = "", 3000);
            }
          }

          scope.initBubble = () => {
            if(bubbleInitTimer){
              $timeout.cancel(bubbleInitTimer);
            }
            bubbleInitTimer = $timeout(() => scope.bubbleClasses = "show animated bounceInUp", 30000);            
          };

          /**
           * 
           * @param {*} value 
           */
          scope.getInput = value => {
            scope.suggestions = [];
            scope.botContext.url = $location.absUrl();
            Bot.ask(value, scope.botContext);
          };

          /**
           * Injects question via button clicked in the Bot interface
           * @param {string} question 
           */
          scope.injectSuggestion = (question, menuKey) => {
            // we use menuKey BOT_MENU_KEY_CLOSE_TICKET on the frontend to identify and intercept this click and show a confirm prompt
            // note that we need to remove the key from the payload because the backend doesn't recognise it as a menu, and would return a null response
            // simplest way: reinject the same question without the menuKeu
            if (menuKey && menuKey === ChatSession.BOT_MENU_KEY_CLOSE_TICKET) {
              return System.confirm({
                title: "Close your support ticket?",
                message: "Are you sure? Your conversaion with the WRP Team will end and you will be passed back to the virtual assistant.",
                buttons: {
                  okButton: "Close ticket",
                  cancelButton: "Keep open",
                }
              })
                .then(() => scope.injectSuggestion(question, null));
            }

            scope.botContext.url = $location.absUrl();
            Bot.ask(question, scope.botContext, menuKey || null);
            ChatSession.useSuggestion(question);
            scope.suggestions = [];
            //scope.refreshSuggestions();
          };

          /**
           * 
           * @param {*} question 
           * @param {*} menuKey 
           */
          scope.injectMenuQuestion = (question, menuKey) => {
            scope.disableMenu = true;

            $timeout(() => {
              scope.botContext.url = $location.absUrl();
              Bot.ask(question, scope.botContext, menuKey);
            }, 250);
            $timeout(() => ChatSession.clearMenuOnUse(), 500);
          }


          /**
           * Injects question from outside the Bot app
           * Note that it can be triggered with no question
           * this has the effect of just opening the Bot panel
           */
          $rootScope.$on(EVENTS.askBotEvent, (e, params) => {
            if (scope.ready) {
              scope.togglePanel(true);
              if (params.question) {
                scope.suggestions = [];
                scope.botContext.url = $location.absUrl();
                // scope.togglePanel(true) opens the panel and if there is not already an active chat session
                // this triggers bot-factory to call /api/2/support/chat/start-session. as this is asynchronous
                // we need to wait till we have a conversation key before asking the question
                let cancelWatch = scope.$watch("session.conversation_key", newValue => {
                  if (newValue) {
                    ChatSession.clearMenu();
                    Bot.ask(params.question, scope.botContext);
                    cancelWatch();
                  }
                });
              }
            }
          });


          scope.onBotMetaCallback = botMetadata => scope.botContext = botMetadata;
          scope.refreshSuggestions = () => scope.suggestions = ChatSession.getSuggestions(scope.botContext);

          scope.activateMapLinks = () => {
            // outside of Angular scope
            if (WRP && WRP.DeliveryMap) {
              WRP.DeliveryMap.initBot('.bot-lg-container');
            }
          }

          scope.scrollToMax = instantMode => {
            if(scrollTimer){
              $timeout.cancel(scrollTimer);
            }
            scrollTimer = $timeout(() => {
              const scrollingElement = document.getElementById("contact-chat-panel");
              if (scrollingElement) {
                if(instantMode){
                  scrollingElement.scrollTop = scrollingElement.scrollHeight;
                } else {
                  scrollingElement.scrollTo({
                    top: scrollingElement.scrollHeight,
                    behavior: 'smooth'
                  });
                }
              }

              scope.activateMapLinks();

            }, 0);
          }

          scope.allReset = () => {
            ChatSession.reset();
            Bot.reset();
          }

          // - - end scope methods --  // 



          // allow for scroll to top button on long products listing pages
          scope.scrollButtonVisible = false;
          $rootScope.$on(EVENTS.scrollButtonEvent, (e, params) => scope.scrollButtonVisible = params.show);

          $rootScope.$on(BOT_EVENTS.chatSessionRefreshed, (e, params) => {

            if(scope.session && scope.session.transcript && scope.session.transcript.length){
              if(scope.session.transcript.length > transcriptLength){
                transcriptLength = scope.session.transcript.length;
                scope.scrollToMax();
              }
              // special case after reset, needed becuase transcriptLength may not have changed but we need to scroll to max anyway
              // but we must not scroll to max EVERY time otherwise even when nothing has changed it scrolls to the bottom
              // which is frustrating for the user as they may have scrolled up to read a previous comment
              if (params && params.didReset) {
                scope.scrollToMax();
              }
              
              if(params && params.fromServer){
                scope.refreshSuggestions();
              }              
            }
            
          });


          $rootScope.$on(EVENTS.activateTicketEvent, (e, params) => {
            if(params.conversation_key){
              activateFromExternalEvent = {
                conversation_key: params.conversation_key
              };
              if(scope.ready){
                ChatSession.reset(params.conversation_key);
                Bot.reset();
                scope.scrollToMax();
                // @NOTE if the external event comes quickly enough then this won't happen
                // but otherwise prevent bubble when coming from an activate ticket link
                scope.dismissBubble();
              }
            }
          });

          // we are using ngExternalEventDispatcher so this is broadcast outside of angular to tell the DOM / jQuery app we are ready
          // because jquery loads first we need to delay this, otherwise the listener is not ready in source/js/products-page.js
          $timeout( () => {
            $rootScope.$broadcast(EVENTS.eventListen, { eventType: EVENTS.angularReady, outbound: true });
            $rootScope.$broadcast(EVENTS.eventListen, { eventType: EVENTS.botPanelState, outbound: true });
            // broadcast of ready event was moved to when BOT_EVENTS.chatSessionReady is triggered by the chat session
            // $rootScope.$broadcast(EVENTS.angularReady, { module: 'bot' });
            // set up listener for *inbound* material change event
            $rootScope.$broadcast(EVENTS.eventListen, { eventType: EVENTS.materialChangeEvent, outbound: false, restrictModule: 'bot' });    
          }, 600);

          // don't show prompt bubble to staff
          $rootScope.$on(BOT_EVENTS.chatSessionReady, () => {
            scope.ready = true;
            $timeout( () => {
              $rootScope.$broadcast(EVENTS.angularReady, { module: 'bot' });
            }, 100);
            scope.scrollToMax();
            if(scope.session && scope.session.userStatus && scope.session.userStatus.user_is_staff){
              scope.dismissBubble();              
            }
            scope.refreshSuggestions();
          });

          $rootScope.$on(EVENTS.materialChangeEvent, (e, params) => {
            scope.botContext.material_id = params.materialId;
            scope.botContext.material_name = params.materialName;
          });


          /**
           * APP STARTS HERE
           */
          System.init().then(
             () => {
              Bot.init(isAlwaysAwakeMode); // sets up listeners
              ChatSession.init(); // starts a new session (or retrieves / restores one)
              // scope.ready = true; // NOTE this is set true on BOT_EVENTS.chatSessionReady
              // on init scroll instantly
              scope.scrollToMax(true);

              if(activateFromExternalEvent){
                ChatSession.reset(activateFromExternalEvent.conversation_key);
                Bot.reset();
                scope.scrollToMax();
              } else {
                if(!isAlwaysAwakeMode){
                  if(!scope.session.bubbleDismissed){
                    scope.initBubble();
                  }
                }
              }

              $rootScope.$on(BOT_EVENTS.doReset, () => $timeout(() => scope.allReset(), 2000));
              $rootScope.$on(BOT_EVENTS.menuStatusEvent, (e, params) => scope.disableMenu = !params.enabled);
              $rootScope.$on(BOT_EVENTS.scrollToEnd, (e, params) => $timeout(() => scope.scrollToMax(), 500));
            },
            reason => System.alert({ title: "An error occurred", message: "An error occurred: " + reason})
          );

        }


      };

    });

